import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { formatDate } from "@/lib/demo-data";
import { Input } from "@/components/ui/input";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { CheckCircle2, AlertCircle, Clock, FileText, XCircle, UserPlus, ChevronLeft, ChevronRight, RefreshCw, MessageSquare, StickyNote, X } from "lucide-react";
import { toast } from "sonner";

const OpsStatusBadge = ({ status }: { status: string }) => {
  const variants: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    NEW: { label: "Nouveau", variant: "default" },
    IN_REVIEW: { label: "En revue", variant: "secondary" },
    REQUESTED_DOCS: { label: "Docs demandés", variant: "outline" },
    WRITING: { label: "Rédaction", variant: "secondary" },
    READY: { label: "Prêt", variant: "default" },
    SUBMITTED: { label: "Soumis", variant: "default" },
    WON: { label: "Gagné", variant: "default" },
    LOST: { label: "Perdu", variant: "destructive" },
    CANCELLED: { label: "Annulé", variant: "destructive" },
  };

  const config = variants[status] || { label: status, variant: "outline" };
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

export default function GoQueue() {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<"ALL" | "NEW" | "URGENT" | "NO_PARTNER">("ALL");
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<{ projectId: string; matchId: string } | null>(null);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>("");
  const [docsDialogOpen, setDocsDialogOpen] = useState(false);
  const [docsTarget, setDocsTarget] = useState<{ projectId: string } | null>(null);
  const [docsMessage, setDocsMessage] = useState("");
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<{ matchId: string } | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  // Notes drawer
  const [notesDrawer, setNotesDrawer] = useState<{ open: boolean; projectId: string; projectTitle: string; notes: string }>({ open: false, projectId: "", projectTitle: "", notes: "" });
  const [newNote, setNewNote] = useState("");

  const { data, isLoading, isError, refetch } = trpc.admin.goQueue.list.useQuery({
    page,
    limit: 20,
    filter,
  });

  const { data: partnersData } = trpc.admin.partners.list.useQuery({
    page: 1,
    limit: 100,
    active: true,
  });

  const createProjectMutation = trpc.admin.goQueue.createProject.useMutation({
    onSuccess: () => {
      toast.success("Projet créé avec succès");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const updateStatusMutation = trpc.admin.goQueue.updateStatus.useMutation({
    onSuccess: () => {
      toast.success("Statut mis à jour");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const requestDocsMutation = trpc.admin.goQueue.requestDocs.useMutation({
    onSuccess: () => {
      toast.success("Documents demandés au client");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const rejectMutation = trpc.admin.goQueue.reject.useMutation({
    onSuccess: () => {
      toast.success("GO rejeté");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const { data: staffList } = trpc.admin.staff.list.useQuery(undefined, { retry: false });

  const assignStaffMutation = trpc.admin.goQueue.assign.useMutation({
    onSuccess: () => {
      toast.success("Assignation mise à jour");
      refetch();
    },
    onError: (err) => toast.error(err.message || "Erreur d'assignation"),
  });

  const addNoteMutation = trpc.admin.goQueue.addNote.useMutation({
    onSuccess: () => {
      toast.success("Note ajoutée");
      setNewNote("");
      refetch();
    },
    onError: (err) => toast.error(err.message || "Erreur ajout note"),
  });

  const assignPartnerMutation = trpc.admin.partners.assignToProject.useMutation({
    onSuccess: () => {
      toast.success("Partenaire assigné avec succès");
      setAssignDialogOpen(false);
      setAssignTarget(null);
      setSelectedPartnerId("");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleCreateProject = (tenderMatchId: string) => {
    createProjectMutation.mutate({ tenderMatchId });
  };

  const handleAssignPartner = () => {
    if (!assignTarget || !selectedPartnerId) return;
    assignPartnerMutation.mutate({
      partnerId: selectedPartnerId,
      projectId: assignTarget.projectId,
    });
  };

  const totalPages = data ? Math.ceil(data.total / 20) : 0;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold">GO Queue</h1>
          <p className="text-muted-foreground">
            Centre de triage — gérer les décisions GO et l'assistance
            {data && ` (${data.total} entrées)`}
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>File d'attente GO</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant={filter === "ALL" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setFilter("ALL"); setPage(1); }}
                >
                  Tous
                </Button>
                <Button
                  variant={filter === "NEW" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setFilter("NEW"); setPage(1); }}
                >
                  Nouveaux
                </Button>
                <Button
                  variant={filter === "URGENT" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setFilter("URGENT"); setPage(1); }}
                >
                  Urgents
                </Button>
                <Button
                  variant={filter === "NO_PARTNER" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setFilter("NO_PARTNER"); setPage(1); }}
                >
                  Sans partenaire
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <ListPageSkeleton />
            ) : isError ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <AlertCircle className="h-12 w-12 text-destructive/40" aria-hidden="true" />
                <p className="text-muted-foreground font-medium">Erreur de chargement de la GO Queue</p>
                <Button variant="outline" onClick={() => refetch()}>
                  <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />Réessayer
                </Button>
              </div>
            ) : data && data.items.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entreprise</TableHead>
                    <TableHead>AO</TableHead>
                    <TableHead>Deadline</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Date GO</TableHead>
                    <TableHead>Statut Ops</TableHead>
                    <TableHead>Assigné à</TableHead>
                    <TableHead>Partenaire</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((match: any) => {
                    const tender = match.Tender;
                    const company = match.Company;
                    const project = match.ResponseProject;
                    if (!tender) return null;

                    const deadline = new Date(tender.deadlineAt);
                    const now = new Date();
                    const daysLeft = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    const isUrgent = daysLeft <= 7;
                    const partnerName = project?.PartnerAssignment?.Partner?.name;

                    return (
                      <TableRow key={match.id} className={`hover:bg-muted/50 transition-colors duration-200 cursor-pointer ${isUrgent ? "bg-destructive/5" : ""}`}>
                        <TableCell className="font-medium">{company?.name || "N/A"}</TableCell>
                        <TableCell className="max-w-xs truncate">{tender.title}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {formatDate(tender.deadlineAt)}
                            {isUrgent && (
                              <Badge variant="destructive" className="text-xs">
                                {daysLeft <= 0 ? "RETARD" : `${daysLeft}j`}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge>{match.score}</Badge>
                        </TableCell>
                        <TableCell>{match.decidedAt ? formatDate(match.decidedAt) : "-"}</TableCell>
                        <TableCell>
                          {project ? (
                            <OpsStatusBadge status={project.opsStatus} />
                          ) : (
                            <Badge variant="outline">À lancer</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {project ? (
                            <div className="flex items-center gap-1">
                              {canAct && staffList ? (
                                <Select
                                  value={project.adminAssigneeId || ""}
                                  onValueChange={(v) => {
                                    if (v) assignStaffMutation.mutate({ projectId: project.id, adminAssigneeId: v });
                                  }}
                                >
                                  <SelectTrigger className="h-7 w-[130px] text-xs">
                                    <SelectValue placeholder="Assigner…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {staffList.map((s: any) => (
                                      <SelectItem key={s.id} value={s.id} className="text-xs">
                                        {s.firstName || s.email}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="text-xs">
                                  {project.User_ResponseProject_adminAssigneeIdToUser
                                    ? `${project.User_ResponseProject_adminAssigneeIdToUser.firstName || project.User_ResponseProject_adminAssigneeIdToUser.email}`
                                    : "—"}
                                </span>
                              )}
                              {project && canAct && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  title="Notes"
                                  onClick={() => setNotesDrawer({
                                    open: true,
                                    projectId: project.id,
                                    projectTitle: tender.title,
                                    notes: project.adminNotes || "",
                                  })}
                                >
                                  <StickyNote className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {partnerName ? (
                            <span className="text-sm">{partnerName}</span>
                          ) : project && canAct ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setAssignTarget({ projectId: project.id, matchId: match.id });
                                setAssignDialogOpen(true);
                              }}
                            >
                              <UserPlus className="mr-1 h-3 w-3" />
                              Assigner
                            </Button>
                          ) : (
                            <Badge variant="outline">-</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {canAct ? (
                            <div className="flex gap-1">
                              {!project ? (
                                <Button
                                  size="sm"
                                  onClick={() => handleCreateProject(match.id)}
                                  disabled={createProjectMutation.isPending}
                                >
                                  <CheckCircle2 className="mr-1 h-3 w-3" />
                                  Créer projet
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setDocsTarget({ projectId: project.id });
                                      setDocsMessage("");
                                      setDocsDialogOpen(true);
                                    }}
                                    disabled={requestDocsMutation.isPending}
                                  >
                                    <FileText className="mr-1 h-3 w-3" />
                                    Docs
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setRejectTarget({ matchId: match.id });
                                      setRejectReason("");
                                      setRejectDialogOpen(true);
                                    }}
                                    disabled={rejectMutation.isPending}
                                  >
                                    <XCircle className="mr-1 h-3 w-3" />
                                    Refuser
                                  </Button>
                                </>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <AlertCircle className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" aria-hidden="true" />
                <p className="text-muted-foreground">Aucun GO en attente</p>
                <p className="text-xs text-muted-foreground mt-1">Les entreprises n'ont pas encore pris de décision GO</p>
              </div>
            )}

            {/* Pagination */}
            {data && data.total > 20 && (
              <div className="flex items-center justify-between border-t pt-4 mt-4">
                <div className="text-sm text-muted-foreground">
                  Affichage de {(page - 1) * 20 + 1} à {Math.min(page * 20, data.total)} sur {data.total}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || isLoading}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Précédent
                  </Button>
                  <div className="text-sm">
                    Page {page} sur {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages || isLoading}
                  >
                    Suivant
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog demande de documents */}
        <Dialog open={docsDialogOpen} onOpenChange={(open) => {
          if (!open) { setDocsDialogOpen(false); setDocsTarget(null); setDocsMessage(""); }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Demander des documents</DialogTitle>
              <DialogDescription>
                Rédigez un message pour le client lui demandant les documents nécessaires.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Message pour le client</Label>
                <Textarea
                  value={docsMessage}
                  onChange={(e) => setDocsMessage(e.target.value)}
                  placeholder="Ex : Merci de fournir votre attestation d'assurance et Kbis..."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDocsDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                onClick={() => {
                  if (docsTarget && docsMessage.trim()) {
                    requestDocsMutation.mutate({ projectId: docsTarget.projectId, message: docsMessage.trim() });
                    setDocsDialogOpen(false);
                    setDocsTarget(null);
                    setDocsMessage("");
                  }
                }}
                disabled={!docsMessage.trim() || requestDocsMutation.isPending}
              >
                {requestDocsMutation.isPending ? "Envoi..." : "Envoyer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog refus GO */}
        <Dialog open={rejectDialogOpen} onOpenChange={(open) => {
          if (!open) { setRejectDialogOpen(false); setRejectTarget(null); setRejectReason(""); }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Refuser ce GO</DialogTitle>
              <DialogDescription>
                Indiquez la raison du refus. Elle sera enregistrée dans le système.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Raison du refus</Label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Ex : Hors périmètre géographique, capacité insuffisante..."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (rejectTarget && rejectReason.trim()) {
                    rejectMutation.mutate({ tenderMatchId: rejectTarget.matchId, reason: rejectReason.trim() });
                    setRejectDialogOpen(false);
                    setRejectTarget(null);
                    setRejectReason("");
                  }
                }}
                disabled={!rejectReason.trim() || rejectMutation.isPending}
              >
                {rejectMutation.isPending ? "Refus..." : "Confirmer le refus"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog assignation partenaire */}
        <Dialog open={assignDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setAssignDialogOpen(false);
            setAssignTarget(null);
            setSelectedPartnerId("");
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assigner un partenaire</DialogTitle>
              <DialogDescription>
                Sélectionnez un partenaire pour ce projet de réponse
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Partenaire</Label>
                <Select value={selectedPartnerId} onValueChange={setSelectedPartnerId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un partenaire..." />
                  </SelectTrigger>
                  <SelectContent>
                    {partnersData?.items.map((partner: any) => (
                      <SelectItem key={partner.id} value={partner.id}>
                        {partner.name} — {partner.skills?.join(", ")} ({partner.zones?.join(", ")})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleAssignPartner}
                disabled={!selectedPartnerId || assignPartnerMutation.isPending}
              >
                {assignPartnerMutation.isPending ? "Assignation..." : "Assigner"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Notes drawer */}
        <Dialog open={notesDrawer.open} onOpenChange={(open) => {
          if (!open) { setNotesDrawer({ open: false, projectId: "", projectTitle: "", notes: "" }); setNewNote(""); }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <StickyNote className="h-4 w-4" />
                Notes — {notesDrawer.projectTitle?.substring(0, 50)}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              {notesDrawer.notes ? (
                <div className="bg-muted/50 rounded-md p-3 max-h-[200px] overflow-y-auto">
                  <pre className="text-sm whitespace-pre-wrap font-sans">{notesDrawer.notes}</pre>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Aucune note pour le moment.</p>
              )}
              <div>
                <Label>Ajouter une note</Label>
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Votre note…"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNotesDrawer({ open: false, projectId: "", projectTitle: "", notes: "" })}>
                Fermer
              </Button>
              <Button
                onClick={() => {
                  if (notesDrawer.projectId && newNote.trim()) {
                    addNoteMutation.mutate({ projectId: notesDrawer.projectId, note: newNote.trim() });
                    setNotesDrawer((prev) => ({ ...prev, notes: prev.notes + `\n\n[${new Date().toISOString()}] ${user?.email}: ${newNote.trim()}` }));
                  }
                }}
                disabled={!newNote.trim() || addNoteMutation.isPending}
              >
                {addNoteMutation.isPending ? "Ajout…" : "Ajouter"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

